import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Loader2, MapPin } from 'lucide-react';
import { ShareBar } from '../components/ShareBar';
import { GuideCard, STUDIO_MAP_EMBED as STUDIO_MAP } from './GuidePages';
import { RegionPackFeature } from '../components/studio/PackFeature';
import { StudioOfflineBanner } from '../components/studio/StudioOfflineBanner';
import { TownAlertForm } from '../components/studio/TownAlertForm';
import { regionSlug } from '../lib/regions';
import { useSeo } from '../lib/seo';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function RegionPage() {
  const { slug } = useParams();
  const [guides, setGuides] = useState(null);
  const [packs, setPacks] = useState([]);
  const [regions, setRegions] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/guides`).then((r) => setGuides(r.data.guides || [])).catch(() => setGuides([]));
    axios.get(`${API}/studio-packs`).then((r) => setPacks(r.data.packs || [])).catch(() => {});
    axios.get(`${API}/studio-regions`).then((r) => setRegions(r.data.regions || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load, slug]);

  const inRegion = (guides || []).filter((g) => regionSlug(g.region) === slug);
  const region = inRegion[0]?.region;
  const info = regions.find((r) => r.slug === slug);
  const regionPacks = info?.pack_slug ? packs.filter((p) => p.slug === info.pack_slug) : packs.filter((p) => regionSlug(p.region) === slug);
  const allRegions = Array.from(new Set((guides || []).map((g) => g.region).filter(Boolean))).sort();

  useSeo(region && { title: `Small Towns of ${region}: Town Guides for Moving to ${inRegion[0]?.country || 'Italy'}`, description: info?.intro || inRegion[0]?.region_intro || `Town guides to the small towns of ${region}, ${inRegion[0]?.country || 'Italy'}, for Australians planning a move.`, image: regionPacks[0]?.cover_image_url || inRegion[0]?.cover_image_url }, [region, info?.intro, regionPacks.length]);

  if (!guides) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;
  if (!region) {
    return (
      <div className="min-h-screen pt-32 container-custom text-center" data-testid="region-not-found">
        <StudioOfflineBanner onBack={load} />
        <p className="text-[#1E1622]/60">We don't have guides for this region yet.</p>
        <Link to="/shop" className="link-gold mt-4 inline-block">Browse all towns</Link>
      </div>
    );
  }

  const intro = info?.intro || inRegion[0]?.region_intro || regionPacks[0]?.description || `${inRegion.length} small towns in ${region}, each with its own guide written for Australians making the move.`;
  return (
    <div className="min-h-screen pb-20" data-testid="region-page">
      <section className="relative pt-32 pb-16 bg-[#2D1037] overflow-hidden">
        <img src={inRegion[0].cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D1037] via-[#2D1037]/90 to-transparent" />
        <div className="relative container-custom">
          <div className="flex flex-wrap gap-6 mb-8">
            <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#E5C178] hover:text-[#C5A059]"><ArrowLeft size={16} /> All towns</Link>
            <Link to={`/shop/countries/${regionSlug(inRegion[0]?.country || 'Italy')}`} data-testid="region-country-link" className="inline-flex items-center gap-2 text-sm text-[#E5C178] hover:text-[#C5A059]">All of {inRegion[0]?.country || 'Italy'} <ArrowRight size={14} /></Link>
          </div>
          <span className="eyebrow mb-4 text-[#C5A059]"><span className="w-10 h-px bg-[#C5A059]" /> Small Towns Series</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#FAF7F2] mb-4" data-testid="region-title">{region}</h1>
          <p className="max-w-2xl text-[#FAF7F2]/75 font-light leading-relaxed mb-8" data-testid="region-intro">{intro}</p>
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm text-[#E5C178] inline-flex items-center gap-2"><MapPin size={16} /> {inRegion.length} town guide{inRegion.length === 1 ? '' : 's'}</span>
            <ShareBar url={`${BACKEND}/api/share/regions/${slug}`} text={`Small Towns of ${region}: town guides for Australians moving to ${inRegion[0]?.country || 'Italy'}`} testId="region-share-bar" />
          </div>
        </div>
      </section>

      <div className="container-custom pt-12">
        <StudioOfflineBanner onBack={load} />
        <div className="rounded-2xl overflow-hidden border border-[#E6DAC5] mb-12" data-testid="region-map">
          <iframe src={info?.map_embed_url || STUDIO_MAP} title={`Map of ${region} towns`} loading="lazy" style={{ width: '100%', height: 480, border: 0 }} />
        </div>
        {regionPacks.map((p) => <RegionPackFeature key={p.id} p={p} />)}
        <p className="eyebrow mb-4"><span className="w-10 h-px bg-[#C5A059]" /> Town guides in {region}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="region-guides">
          {inRegion.map((g) => <GuideCard key={g.id} g={g} />)}
        </div>
        <div className="max-w-2xl mb-16">
          <TownAlertForm regions={allRegions} initialRegions={[region]} dark title={`New towns in ${region}`} />
        </div>

        {allRegions.length > 1 && (
          <div data-testid="other-regions">
            <p className="eyebrow mb-4"><span className="w-10 h-px bg-[#C5A059]" /> Explore other regions</p>
            <div className="flex flex-wrap gap-2">
              {allRegions.filter((r) => r !== region).map((r) => (
                <Link key={r} to={`/shop/regions/${regionSlug(r)}`} data-testid={`other-region-${regionSlug(r)}`}
                  className="px-4 py-2 rounded-full text-sm border border-[#E6DAC5] bg-white text-[#1E1622]/70 transition-colors duration-200 hover:border-[#C5A059] hover:text-[#3B164C]">{r}</Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
